import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Network, Plus, Trash2, Download, Image as ImageIcon, Printer, Maximize2, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { SELECT_CLASS } from './ElementEditModal';
import { useMobileLayout } from '../../hooks/use-mobile';
import {
  EL_ID, buildFuehrungsharke, buildFunkskizze, downloadSvg, downloadPng, printSvg
} from '../../lib/fuehrungsDiagramm';

const FieldInput = ({ value, onChange, placeholder, testId, maxLength = 60, className = '' }) => (
  <Input
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    maxLength={maxLength}
    className={`h-8 touch:h-10 text-sm ${className}`}
    data-testid={testId}
  />
);

const DiagramView = ({ diagram, filename, title, testId }) => {
  // Auf dem Handy passt das Diagramm zunächst in die Breite, „100 %“ zeigt es in Originalgröße
  const isMobile = useMobileLayout();
  const [fit, setFit] = useState(isMobile);

  const handlePng = async () => {
    try {
      await downloadPng(diagram, filename);
    } catch (error) {
      console.error('PNG-Export fehlgeschlagen:', error);
      toast.error('PNG-Export fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => downloadSvg(diagram, filename)} data-testid={`${testId}-svg`}>
          <Download className="w-4 h-4 mr-2" />SVG
        </Button>
        <Button size="sm" variant="outline" onClick={handlePng} data-testid={`${testId}-png`}>
          <ImageIcon className="w-4 h-4 mr-2" />PNG
        </Button>
        <Button size="sm" variant="outline" onClick={() => printSvg(diagram, title)} data-testid={`${testId}-print`}>
          <Printer className="w-4 h-4 mr-2" />Drucken
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setFit(!fit)} data-testid={`${testId}-fit`}>
          {fit ? <Maximize2 className="w-4 h-4 mr-2" /> : <Minimize2 className="w-4 h-4 mr-2" />}
          {fit ? '100 %' : 'Einpassen'}
        </Button>
      </div>
      {/* SVG wird in fuehrungsDiagramm.js erzeugt, alle Texte sind escaped */}
      <div
        className={`border border-border rounded-md overflow-auto max-h-[55dvh] bg-white${fit ? ' [&>svg]:max-w-full [&>svg]:h-auto' : ''}`}
        data-testid={testId}
        dangerouslySetInnerHTML={{ __html: diagram.svg }}
      />
    </div>
  );
};

export const FuehrungModal = ({
  open,
  onOpenChange,
  organisation,
  onOrganisationChange,
  vehicles,
  onUpdateVehicle,
  scenarioName,
}) => {
  const el = organisation.einsatzleitung;

  const setEl = (patch) => onOrganisationChange({ ...organisation, einsatzleitung: { ...el, ...patch } });

  const setAbschnitt = (id, patch) => onOrganisationChange({
    ...organisation,
    abschnitte: organisation.abschnitte.map(a => (a.id === id ? { ...a, ...patch } : a)),
  });

  const addAbschnitt = () => onOrganisationChange({
    ...organisation,
    abschnitte: [
      ...organisation.abschnitte,
      { id: uuidv4(), name: `EA ${organisation.abschnitte.length + 1}`, fuehrer: '', funkrufname: '', gruppe: '' },
    ],
  });

  const removeAbschnitt = (id) => {
    onOrganisationChange({ ...organisation, abschnitte: organisation.abschnitte.filter(a => a.id !== id) });
    vehicles.filter(v => v.properties.abschnittId === id).forEach(v => onUpdateVehicle(v.id, { abschnittId: '' }));
  };

  const titleSuffix = scenarioName ? ` – ${scenarioName}` : '';
  const harke = useMemo(
    () => (open ? buildFuehrungsharke({ organisation, vehicles, title: `Führungsharke${titleSuffix}` }) : null),
    [open, organisation, vehicles, titleSuffix]
  );
  const funk = useMemo(
    () => (open ? buildFunkskizze({ organisation, vehicles, title: `Funkskizze${titleSuffix}` }) : null),
    [open, organisation, vehicles, titleSuffix]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Auf dem Handy als Vollbild, sonst großer Dialog */}
      <DialogContent
        className="sm:max-w-5xl sm:max-h-[92dvh] max-sm:w-full max-sm:h-dvh max-sm:max-h-none max-sm:rounded-none max-sm:border-0 max-sm:pt-[calc(1.5rem+env(safe-area-inset-top))] max-sm:pb-[calc(1.5rem+env(safe-area-inset-bottom))] content-start overflow-y-auto"
        data-testid="fuehrung-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2 text-lunefire-charcoal dark:text-white">
            <Network className="w-5 h-5 text-lunefire-red" />
            Führungsstruktur
          </DialogTitle>
          <DialogDescription className="font-body">
            Einsatzabschnitte, Funkrufnamen und Funkgruppen gelten nur für dieses Szenario und werden mit ihm gespeichert.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="struktur">
          <TabsList className="max-sm:w-full max-sm:grid max-sm:grid-cols-3">
            <TabsTrigger value="struktur" data-testid="tab-struktur">Struktur</TabsTrigger>
            <TabsTrigger value="harke" data-testid="tab-harke">Führungsharke</TabsTrigger>
            <TabsTrigger value="funk" data-testid="tab-funk">Funkskizze</TabsTrigger>
          </TabsList>

          <TabsContent value="struktur" className="space-y-5 pt-2">
            {/* Einsatzleitung */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Einsatzleitung</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                <FieldInput value={el.name} onChange={v => setEl({ name: v })} placeholder="Bezeichnung" testId="el-name" />
                <FieldInput value={el.fuehrer} onChange={v => setEl({ fuehrer: v })} placeholder="Einsatzleiter (Name)" testId="el-fuehrer" />
                <FieldInput value={el.funkrufname} onChange={v => setEl({ funkrufname: v })} placeholder="Funkrufname" testId="el-funkrufname" />
                <FieldInput value={el.gruppe} onChange={v => setEl({ gruppe: v })} placeholder="Funkgruppe Führung (z.B. TMO 401)" testId="el-gruppe" />
              </div>
            </section>

            {/* Einsatzabschnitte */}
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Einsatzabschnitte</h3>
                <Button size="sm" variant="outline" onClick={addAbschnitt} data-testid="add-abschnitt-btn">
                  <Plus className="w-4 h-4 mr-1" />Abschnitt
                </Button>
              </div>
              {organisation.abschnitte.length === 0 && (
                <p className="text-xs text-muted-foreground">Noch keine Einsatzabschnitte angelegt.</p>
              )}
              {organisation.abschnitte.map(a => (
                <div
                  key={a.id}
                  className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center max-md:rounded-md max-md:border max-md:border-border max-md:p-2"
                  data-testid="abschnitt-row"
                >
                  <FieldInput value={a.name} onChange={v => setAbschnitt(a.id, { name: v })} placeholder="Name (z.B. EA Nord)" testId="abschnitt-name" />
                  <FieldInput value={a.fuehrer} onChange={v => setAbschnitt(a.id, { fuehrer: v })} placeholder="Abschnittsleiter (Name)" testId="abschnitt-fuehrer" className="max-md:col-span-2" />
                  <FieldInput value={a.funkrufname} onChange={v => setAbschnitt(a.id, { funkrufname: v })} placeholder="Funkrufname" testId="abschnitt-funkrufname" className="max-md:col-span-2" />
                  <FieldInput value={a.gruppe} onChange={v => setAbschnitt(a.id, { gruppe: v })} placeholder="Funkgruppe (z.B. TMO 402)" testId="abschnitt-gruppe" className="max-md:col-span-2" />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 touch:h-10 touch:w-10 text-lunefire-red hover:bg-lunefire-red/10 max-md:row-start-1 max-md:col-start-2"
                    onClick={() => removeAbschnitt(a.id)}
                    title="Abschnitt entfernen"
                    aria-label="Abschnitt entfernen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </section>

            {/* Fahrzeuge */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Fahrzeuge auf der Karte</h3>
              {vehicles.length === 0 && (
                <p className="text-xs text-muted-foreground">Noch keine Fahrzeuge auf der Karte platziert.</p>
              )}
              {vehicles.map(v => (
                <div
                  key={v.id}
                  className="grid grid-cols-2 md:grid-cols-[8rem_1fr_1fr_1fr] gap-2 items-center max-md:rounded-md max-md:border max-md:border-border max-md:p-2"
                  data-testid="vehicle-row"
                >
                  <span className="text-sm font-medium truncate" title={v.properties.name}>{v.properties.name}</span>
                  <FieldInput
                    value={v.properties.customName || ''}
                    onChange={val => onUpdateVehicle(v.id, { customName: val })}
                    placeholder="Bezeichnung"
                    testId="vehicle-name"
                  />
                  <FieldInput
                    value={v.properties.callsign || ''}
                    onChange={val => onUpdateVehicle(v.id, { callsign: val })}
                    placeholder="Funkrufname"
                    maxLength={40}
                    testId="vehicle-callsign"
                  />
                  <select
                    value={v.properties.abschnittId || ''}
                    onChange={(e) => onUpdateVehicle(v.id, { abschnittId: e.target.value })}
                    className={`${SELECT_CLASS} h-8 touch:h-10`}
                    data-testid="vehicle-abschnitt"
                  >
                    <option value="">– nicht zugeordnet –</option>
                    <option value={EL_ID}>Direkt der Einsatzleitung</option>
                    {organisation.abschnitte.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </section>
          </TabsContent>

          <TabsContent value="harke" className="pt-2">
            {harke && <DiagramView diagram={harke} filename="fuehrungsharke" title="Führungsharke" testId="harke" />}
          </TabsContent>

          <TabsContent value="funk" className="pt-2">
            {funk && <DiagramView diagram={funk} filename="funkskizze" title="Funkskizze" testId="funk" />}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default FuehrungModal;
